import type { UTCTimestamp } from 'lightweight-charts';

import type { ChartTrace } from './types';

export type ChartPeriod = '5D' | '1M' | '1Y' | '3Y' | '5Y' | 'Max';

export const CHART_PERIODS: ChartPeriod[] = ['5D', '1M', '1Y', '3Y', '5Y', 'Max'];

export const DEFAULT_CHART_PERIOD: ChartPeriod = '3Y';

const SECONDS_PER_DAY = 86_400;

const PERIOD_DAYS: Record<Exclude<ChartPeriod, 'Max'>, number> = {
  '5D': 5,
  '1M': 30,
  '1Y': 365,
  '3Y': 365 * 3,
  '5Y': 365 * 5,
};

export interface VisibleRangeSeconds {
  fromEpoch: UTCTimestamp;
  toEpoch: UTCTimestamp;
}

export const computeAnchorEpoch = (traces: ChartTrace[]): number | null => {
  let max = -Infinity;
  for (const trace of traces) {
    if (trace.kind !== 'series') continue;
    for (const point of trace.points) {
      const t = point.time as number;
      if (t > max) max = t;
    }
  }
  return Number.isFinite(max) ? max : null;
};

const FALLBACK_MAX_DAYS = 365;

export const computeFallbackRangeSeconds = (
  period: ChartPeriod,
  now: number = Math.floor(Date.now() / 1000),
): VisibleRangeSeconds => {
  const days = period === 'Max' ? FALLBACK_MAX_DAYS : PERIOD_DAYS[period];
  const span = days * SECONDS_PER_DAY;
  return {
    fromEpoch: (now - span) as UTCTimestamp,
    toEpoch: now as UTCTimestamp,
  };
};

export const computeVisibleRangeSeconds = (
  traces: ChartTrace[],
  period: ChartPeriod,
): VisibleRangeSeconds | null => {
  const anchor = computeAnchorEpoch(traces);
  if (anchor !== null) {
    if (period === 'Max') return null;
    const span = PERIOD_DAYS[period] * SECONDS_PER_DAY;
    return {
      fromEpoch: (anchor - span) as UTCTimestamp,
      toEpoch: anchor as UTCTimestamp,
    };
  }
  const hasScalar = traces.some((t) => t.kind === 'scalar');
  if (hasScalar) return computeFallbackRangeSeconds(period);
  return null;
};

const computeSeriesDataSpan = (
  traces: ChartTrace[],
): VisibleRangeSeconds | null => {
  let min = Infinity;
  let max = -Infinity;
  for (const trace of traces) {
    if (trace.kind !== 'series') continue;
    for (const point of trace.points) {
      const t = point.time as number;
      if (t < min) min = t;
      if (t > max) max = t;
    }
  }
  if (!Number.isFinite(min) || !Number.isFinite(max)) return null;
  return { fromEpoch: min as UTCTimestamp, toEpoch: max as UTCTimestamp };
};

export const computeBroadcastRange = (
  traces: ChartTrace[],
  period: ChartPeriod,
): VisibleRangeSeconds | null => {
  const visible = computeVisibleRangeSeconds(traces, period);
  if (visible !== null) return visible;
  return computeSeriesDataSpan(traces);
};
